import React from 'react';
import {
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import {
  TableRows as TableIcon,
  GridView as CardIcon,
} from '@mui/icons-material';
import { getFlatPortfolioColors, portfolioTypography } from '../../../../utils/getFlatPortfolioColors';

export type ViewMode = 'table' | 'cards';

interface ModeConfig {
  id: ViewMode;
  label: string;
  icon: React.ReactNode;
}

interface ViewModeToggleProps {
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  disabled?: boolean;
}

const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  mode,
  onModeChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);

  const modes: ModeConfig[] = [
    {
      id: 'table',
      label: 'TABLE',
      icon: <TableIcon />,
    },
    {
      id: 'cards',
      label: 'CARDS',
      icon: <CardIcon />,
    },
  ];

  return (
    <Box
      sx={{
        display: 'inline-flex',
        border: `1px solid ${colors.border.strong}`,
        borderRadius: 0, // Sharp corners - no rounding
        overflow: 'hidden',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        // NO shadows, NO gradients, NO blur - pure flat design
      }}
    >
      {modes.map((modeConfig, index) => (
        <Box
          key={modeConfig.id}
          onClick={() => !disabled && onModeChange(modeConfig.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 2,
            py: 1,
            minWidth: 80,
            cursor: disabled ? 'default' : 'pointer',
            transition: 'background-color 0.2s linear, color 0.2s linear',
            backgroundColor: mode === modeConfig.id
              ? colors.functional.success
              : 'transparent',
            borderRight: index < modes.length - 1 
              ? `1px solid ${colors.border.strong}`
              : 'none',
            '&:hover': {
              backgroundColor: mode === modeConfig.id
                ? colors.functional.successDark
                : colors.background.hover,
            },
            // NO gradients, NO shadows, NO transforms - pure flat design
          }}
        >
          <Box
            sx={{
              fontSize: 16,
              color: mode === modeConfig.id
                ? '#ffffff'
                : colors.text.secondary,
              transition: 'color 0.2s linear',
              '& svg': {
                fontSize: 'inherit',
              },
            }}
          >
            {modeConfig.icon}
          </Box>
          <Typography
            sx={{
              ...portfolioTypography.label,
              fontSize: '0.75rem',
              color: mode === modeConfig.id
                ? '#ffffff'
                : colors.text.secondary,
              fontWeight: mode === modeConfig.id ? 600 : 400,
              transition: 'color 0.2s linear',
            }}
          >
            {modeConfig.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default ViewModeToggle;